export type MarketplaceSlaHint = {
  p95_exec_ms?: number;
  timeout_rate_7d?: number;
};

export type MarketplaceEtaHint = {
  queue_p50_s?: number;
  exec_p50_s?: number;
  exec_p95_s?: number;
  sample_size_7d?: number;
  updated_at?: string;
};

export type MarketplaceFileRole = {
  role: string;
  description?: string;
  accepted_types?: string[];
  possible_types?: string[];
  required?: boolean;
  guaranteed?: boolean;
  max_size_bytes?: number;
  max_count?: number;
};

export type MarketplaceAttachments = {
  accepts_files?: boolean;
  includes_files?: boolean;
  max_files?: number;
  max_total_size_bytes?: number;
  accepted_mime_types?: string[];
  possible_mime_types?: string[];
  file_roles?: MarketplaceFileRole[];
};

export type MarketplaceAttachmentRef = {
  role: string;
  filename?: string;
  mime_type?: string;
};

export type MarketplaceInputExample = {
  title?: string;
  description?: string;
  params?: unknown;
  attachments?: MarketplaceAttachmentRef[];
};

export type MarketplaceOutputExample = {
  title?: string;
  description?: string;
  result?: unknown;
  attachments?: MarketplaceAttachmentRef[];
};

export type MarketplaceTemplateBundle = {
  template_ref: string;
  input_schema?: unknown;
  output_schema?: unknown;
  input_attachments?: MarketplaceAttachments | null;
  output_attachments?: MarketplaceAttachments | null;
  input_examples?: MarketplaceInputExample[] | null;
  output_examples?: MarketplaceOutputExample[] | null;
  example_contract?: unknown;
  example_result?: unknown;
  readme_markdown?: string | null;
  template_version?: string | null;
};

export type MarketplaceHotline = {
  hotline_id: string;
  responder_id: string;
  responder_slug?: string;
  hotline_slug?: string;
  display_name: string;
  summary?: string | null;
  description?: string | null;
  status: string;
  availability_status: string;
  last_heartbeat_at?: string | null;
  responder_public_key_pem?: string | null;
  template_ref: string;
  capabilities?: string[];
  task_types?: string[];
  tags?: string[];
  version?: string | null;
  sla_hint?: MarketplaceSlaHint | null;
  eta_hint?: MarketplaceEtaHint | null;
  recommended_for?: string[] | null;
  not_recommended_for?: string[] | null;
  limitations?: string[] | null;
  input_summary?: string | null;
  output_summary?: string | null;
  input_attachments?: MarketplaceAttachments | null;
  output_attachments?: MarketplaceAttachments | null;
  trust_badges?: string[];
  updated_at?: string | null;
  input_schema?: unknown;
  output_schema?: unknown;
  related_hotlines?: MarketplaceHotline[];
  responder_profile?: {
    responder_id: string;
    display_name?: string;
    support_email?: string | null;
    availability_status?: string | null;
    last_heartbeat_at?: string | null;
  } | null;
};

export type MarketplaceResponder = {
  responder_id: string;
  responder_slug?: string;
  display_name: string;
  hotline_count: number;
  capabilities: string[];
  hotlines?: MarketplaceHotline[];
};

export const FALLBACK_HOTLINES: MarketplaceHotline[] = [
  {
    hotline_id: "starlight.creative.studio.v1",
    responder_id: "responder_starlight",
    responder_slug: "starlight",
    hotline_slug: "creative-studio",
    display_name: "DEMO · Starlight AI 创意工坊",
    summary: "示例预览条目，用来展示 Hotline 目录字段；当前不可直接调用。提交创作提示词，AI 后端多模型编排管线生成创意图像。提示词是公开的，生成智能是私有的。",
    description: "Starlight AI 创意工坊展示了 Hotline 的核心价值：Caller 只需提供文字描述，Responder 后端的多阶段模型编排、专有 Prompt 增强模板、风格迁移算法和微调模型全部私有运行。参考图仅作软风格信号，不作直接条件输入。这是「对外发布接口，不是大脑」的典型实现。",
    status: "enabled",
    task_types: ["creative_generation", "image_synthesis"],
    capabilities: ["image.generate", "style.transfer", "multi_model.orchestrate"],
    tags: ["creative", "image", "ai-generation", "design"],
    availability_status: "demo",
    last_heartbeat_at: null,
    responder_public_key_pem: "-----BEGIN PUBLIC KEY-----STARLIGHTCREATIVE-----END PUBLIC KEY-----",
    template_ref: "docs/templates/hotlines/starlight.creative.studio.v1/",
    version: "v1",
    recommended_for: [
      "产品摄影和电商视觉素材生成",
      "营销 Banner 和社交媒体内容创作",
      "概念图和创意探索",
      "品牌一致性视觉资产批量生产",
      "视觉营销方案的快速原型制作"
    ],
    not_recommended_for: [
      "需要实时修改循环的交互式编辑工作流",
      "需要严格法律准确性的图示（医疗、法律、金融图表）",
      "无专项协议情况下的超高分辨率印刷生产（> 4K）"
    ],
    limitations: [
      "最大提示词长度：2,000 字符",
      "每次调用最多生成 4 个变体",
      "参考图仅作为软风格信号，不作为直接条件输入",
      "pipeline_stage_count 不透露具体阶段详情"
    ],
    input_summary: "提供创作提示词（必需，最大 2,000 字符），可选指定风格预设、输出格式、生成数量和宽高比，可附带可选参考图",
    output_summary: "返回 generated_items 数组（含附件角色引用和尺寸），所有图像文件通过输出附件返回，至少保证一个资产",
    input_attachments: {
      accepts_files: true,
      max_files: 1,
      max_total_size_bytes: 10485760,
      accepted_mime_types: ["image/jpeg", "image/png", "image/webp"],
      file_roles: [
        {
          role: "reference_image",
          required: false,
          description: "可选参考图，用于风格或构图引导（最大 10MB）",
          accepted_types: ["image/jpeg", "image/png", "image/webp"],
          max_size_bytes: 10485760
        }
      ]
    },
    output_attachments: {
      includes_files: true,
      max_files: 4,
      max_total_size_bytes: 52428800,
      possible_mime_types: ["image/jpeg", "image/png", "image/webp"],
      file_roles: [
        { role: "generated_asset_1", guaranteed: true, description: "第一张生成图（quantity >= 1 时必返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] },
        { role: "generated_asset_2", guaranteed: false, description: "第二张生成图（quantity >= 2 时返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] },
        { role: "generated_asset_3", guaranteed: false, description: "第三张生成图（quantity >= 3 时返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] },
        { role: "generated_asset_4", guaranteed: false, description: "第四张生成图（quantity = 4 时返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] }
      ]
    },
    trust_badges: ["demo_preview"],
    sla_hint: { p95_exec_ms: 18000, timeout_rate_7d: 0.012 },
    eta_hint: { queue_p50_s: 3, exec_p50_s: 14, exec_p95_s: 30, sample_size_7d: 540, updated_at: "2026-03-25T10:00:00Z" },
    updated_at: "2026-03-25T10:00:00Z"
  },
  {
    hotline_id: "atlas.knowledge.qa.v1",
    responder_id: "responder_atlas",
    responder_slug: "atlas",
    hotline_slug: "knowledge-qa",
    display_name: "DEMO · Atlas 企业知识问答",
    summary: "示例预览条目，用来展示 Hotline 目录字段；当前不可直接调用。提问，从企业私有知识库获得有据可查的回答与引用来源。问题是公开的，知识是私有的。",
    description: "Atlas 企业知识问答展示了 Hotline 的第二种典型模式：Caller 只发送一个问题字符串，Responder 的私有知识语料库、检索策略、领域微调嵌入模型和重排序算法全部私有运行，知识本身永远不会向外流出。适合企业将内部 HR、政策、合规、技术文档等知识服务化。",
    status: "enabled",
    task_types: ["knowledge_qa", "rag_retrieval"],
    capabilities: ["knowledge.retrieve", "qa.grounded", "multilingual"],
    tags: ["enterprise", "rag", "knowledge-base", "qa"],
    availability_status: "demo",
    last_heartbeat_at: null,
    responder_public_key_pem: "-----BEGIN PUBLIC KEY-----ATLASKNOWLEDGEQA-----END PUBLIC KEY-----",
    template_ref: "docs/templates/hotlines/atlas.knowledge.qa.v1/",
    version: "v1",
    recommended_for: [
      "企业内部 HR、政策、合规问答",
      "客户支持知识库检索",
      "工程团队技术文档助手",
      "新员工入职知识查询",
      "专有文档集合的研究综合"
    ],
    not_recommended_for: [
      "需要实时外部数据的问题（股价、实时新闻）",
      "需要严格逐字法律引用的任务",
      "开放域通用知识问答（更适合通用 LLM）"
    ],
    limitations: [
      "最大问题长度：1,000 字符",
      "retrieval_coverage: 'none' 表示知识库无相关覆盖",
      "置信度低于 0.6 时请谨慎对待答案",
      "sources[].excerpt 可能因策略限制被省略"
    ],
    input_summary: "提供自然语言问题（必需，最大 1,000 字符），可选指定响应语言、最大引用数量和检索焦点提示，无文件附件",
    output_summary: "返回 answer（有据可查的自然语言回答）、confidence（0.0–1.0）、sources（引用列表）、可选 follow_up_questions 和 retrieval_coverage，无文件附件",
    trust_badges: ["demo_preview"],
    sla_hint: { p95_exec_ms: 4500, timeout_rate_7d: 0.004 },
    eta_hint: { queue_p50_s: 1, exec_p50_s: 3, exec_p95_s: 8, sample_size_7d: 820, updated_at: "2026-03-25T09:30:00Z" },
    updated_at: "2026-03-25T09:30:00Z"
  },
  {
    hotline_id: "pixel.product.renderer.v1",
    responder_id: "responder_pixel",
    responder_slug: "pixel",
    hotline_slug: "product-renderer",
    display_name: "DEMO · Pixel 产品图渲染器",
    summary: "示例预览条目，用来展示 Hotline 目录字段；当前不可直接调用。上传产品照片，私有微调渲染模型生成高质量电商视觉素材。产品是公开的，渲染智能是私有的。",
    description: "Pixel 产品图渲染器展示了 Hotline 文件输入+图像输出模式：Caller 上传产品照片和描述，Responder 的专有微调渲染模型、背景合成算法、多道合成管线和后处理预设全部私有运行。源照片处理后不被存储。适合电商和品牌客户批量生产高质量产品视觉素材。",
    status: "enabled",
    task_types: ["product_render", "image_compositing"],
    capabilities: ["image.render", "background.synthesis", "product.retouch"],
    tags: ["ecommerce", "product-photo", "rendering", "image-ai"],
    availability_status: "demo",
    last_heartbeat_at: null,
    responder_public_key_pem: "-----BEGIN PUBLIC KEY-----PIXELPRODUCTRENDER-----END PUBLIC KEY-----",
    template_ref: "docs/templates/hotlines/pixel.product.renderer.v1/",
    version: "v1",
    recommended_for: [
      "电商平台产品详情页视觉素材",
      "产品发布营销资产批量生产",
      "多角度产品渲染用于目录生成",
      "广告投放的快速视觉 A/B 测试",
      "经销商白牌产品摄影替换"
    ],
    not_recommended_for: [
      "需要物理触感细节精确还原的产品（如面料质地认证）",
      "严格的工程制图或技术线图",
      "视频或动态产品渲染（仅生成静态图像）"
    ],
    limitations: [
      "源照片最大大小：15MB",
      "支持输入格式：JPEG、PNG、WebP",
      "每次调用最多生成 4 张渲染图",
      "background_removal_applied: false 表示背景复杂，建议预先裁剪"
    ],
    input_summary: "提供产品描述（必需，最大 1,500 字符）和 product_photo 附件（必需，最大 15MB），可选指定背景风格、角度、分辨率和数量",
    output_summary: "返回 renders 数组（含附件角色引用和风格信息），所有图像通过输出附件返回，至少返回一张渲染图",
    input_attachments: {
      accepts_files: true,
      max_files: 1,
      max_total_size_bytes: 15728640,
      accepted_mime_types: ["image/jpeg", "image/png", "image/webp"],
      file_roles: [
        {
          role: "product_photo",
          required: true,
          description: "待渲染的产品照片（JPEG/PNG/WebP，最大 15MB）",
          accepted_types: ["image/jpeg", "image/png", "image/webp"],
          max_size_bytes: 15728640
        }
      ]
    },
    output_attachments: {
      includes_files: true,
      max_files: 4,
      max_total_size_bytes: 52428800,
      possible_mime_types: ["image/jpeg", "image/png", "image/webp"],
      file_roles: [
        { role: "rendered_image_1", guaranteed: true, description: "第一张渲染图（quantity >= 1 时必返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] },
        { role: "rendered_image_2", guaranteed: false, description: "第二张渲染图（quantity >= 2 时返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] },
        { role: "rendered_image_3", guaranteed: false, description: "第三张渲染图（quantity >= 3 时返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] },
        { role: "rendered_image_4", guaranteed: false, description: "第四张渲染图（quantity = 4 时返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] }
      ]
    },
    trust_badges: ["demo_preview"],
    sla_hint: { p95_exec_ms: 28000, timeout_rate_7d: 0.015 },
    eta_hint: { queue_p50_s: 4, exec_p50_s: 20, exec_p95_s: 45, sample_size_7d: 310, updated_at: "2026-03-25T11:00:00Z" },
    updated_at: "2026-03-25T11:00:00Z"
  }
];

export const FALLBACK_TEMPLATE_BUNDLES: Record<string, MarketplaceTemplateBundle> = {
  "starlight.creative.studio.v1": {
    template_ref: "docs/templates/hotlines/starlight.creative.studio.v1/",
    input_schema: {
      type: "object",
      required: ["prompt"],
      properties: {
        prompt: { type: "string", description: "创作提示词，自然语言描述期望的创意产出", minLength: 1, maxLength: 2000 },
        style_preset: { type: "string", enum: ["cinematic", "illustration", "product_clean", "editorial", "abstract", "anime", "photorealistic"], default: "photorealistic", description: "风格预设" },
        output_format: { type: "string", enum: ["jpeg", "png", "webp"], default: "jpeg", description: "输出格式" },
        quantity: { type: "integer", minimum: 1, maximum: 4, default: 1, description: "生成变体数量（1–4）" },
        aspect_ratio: { type: "string", enum: ["1:1", "4:3", "16:9", "9:16", "3:4"], default: "1:1", description: "输出宽高比" },
        reference_image_hint: { type: "string", const: "reference_image", description: "参考图附件角色名（可选）" }
      },
      additionalProperties: false
    },
    output_schema: {
      type: "object",
      required: ["generated_items", "metadata"],
      properties: {
        generated_items: {
          type: "array",
          minItems: 1,
          items: {
            type: "object",
            required: ["attachment_role", "format", "width", "height"],
            properties: {
              attachment_role: { type: "string" },
              format: { type: "string" },
              width: { type: "integer" },
              height: { type: "integer" },
              seed: { type: "integer" }
            }
          }
        },
        metadata: {
          type: "object",
          required: ["generation_time_ms"],
          properties: {
            generation_time_ms: { type: "integer" },
            pipeline_stage_count: { type: "integer" },
            model_version: { type: "string" },
            style_applied: { type: "string" }
          }
        }
      },
      additionalProperties: false
    },
    input_attachments: {
      accepts_files: true,
      max_files: 1,
      max_total_size_bytes: 10485760,
      accepted_mime_types: ["image/jpeg", "image/png", "image/webp"],
      file_roles: [
        { role: "reference_image", required: false, description: "可选参考图，用于风格或构图引导（最大 10MB）", accepted_types: ["image/jpeg", "image/png", "image/webp"], max_size_bytes: 10485760 }
      ]
    },
    output_attachments: {
      includes_files: true,
      max_files: 4,
      max_total_size_bytes: 52428800,
      possible_mime_types: ["image/jpeg", "image/png", "image/webp"],
      file_roles: [
        { role: "generated_asset_1", guaranteed: true, description: "第一张生成图（quantity >= 1 时必返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] },
        { role: "generated_asset_2", guaranteed: false, description: "第二张生成图（quantity >= 2 时返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] },
        { role: "generated_asset_3", guaranteed: false, description: "第三张生成图（quantity >= 3 时返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] },
        { role: "generated_asset_4", guaranteed: false, description: "第四张生成图（quantity = 4 时返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] }
      ]
    },
    input_examples: [
      {
        title: "产品宣传图生成",
        description: "生成一款高端护肤品的产品宣传图，无参考图",
        params: { prompt: "一款高端护肤精华的产品宣传图，背景简洁纯白，柔和的影棚光线，高端编辑风格", style_preset: "product_clean", output_format: "jpeg", quantity: 2, aspect_ratio: "1:1" },
        attachments: []
      }
    ],
    output_examples: [
      {
        title: "双变体生成结果",
        result: {
          generated_items: [
            { attachment_role: "generated_asset_1", format: "jpeg", width: 1024, height: 1024, seed: 8472910 },
            { attachment_role: "generated_asset_2", format: "jpeg", width: 1024, height: 1024, seed: 8472911 }
          ],
          metadata: { generation_time_ms: 14200, pipeline_stage_count: 5, model_version: "starlight-v3.2", style_applied: "product_clean" }
        },
        attachments: [
          { role: "generated_asset_1", filename: "render_001.jpg", mime_type: "image/jpeg" },
          { role: "generated_asset_2", filename: "render_002.jpg", mime_type: "image/jpeg" }
        ]
      }
    ],
    readme_markdown: "# Starlight AI 创意工坊\n\n提交创作提示词，AI 后端多模型编排管线生成创意图像。提示词是公开的，生成智能是私有的。多阶段模型编排、Prompt 增强模板、风格迁移算法和微调模型全部私有运行。",
    template_version: "1.0.0"
  },
  "atlas.knowledge.qa.v1": {
    template_ref: "docs/templates/hotlines/atlas.knowledge.qa.v1/",
    input_schema: {
      type: "object",
      required: ["question"],
      properties: {
        question: { type: "string", description: "自然语言问题", minLength: 1, maxLength: 1000 },
        language: { type: "string", pattern: "^[a-z]{2}(-[A-Z]{2})?$", default: "zh-CN", description: "响应语言 BCP-47 标签" },
        max_sources: { type: "integer", minimum: 1, maximum: 10, default: 3, description: "最大引用数量（1–10，默认 3）" },
        context_hint: { type: "string", maxLength: 200, description: "可选，领域或主题提示（如 'HR policy'、'Q3 financial report'）" }
      },
      additionalProperties: false
    },
    output_schema: {
      type: "object",
      required: ["answer", "confidence"],
      properties: {
        answer: { type: "string", description: "基于私有知识库的回答" },
        confidence: { type: "number", minimum: 0, maximum: 1, description: "置信度（0.0–1.0）" },
        sources: {
          type: "array",
          items: {
            type: "object",
            required: ["title", "relevance_score"],
            properties: {
              title: { type: "string" },
              excerpt: { type: "string" },
              relevance_score: { type: "number", minimum: 0, maximum: 1 }
            }
          }
        },
        follow_up_questions: { type: "array", items: { type: "string" }, maxItems: 3 },
        retrieval_coverage: { type: "string", enum: ["full", "partial", "none"] }
      },
      additionalProperties: false
    },
    input_attachments: { accepts_files: false },
    output_attachments: { includes_files: false },
    input_examples: [
      {
        title: "差旅报销政策查询",
        description: "查询公司差旅报销标准",
        params: { question: "我们公司对员工出差报销的标准是什么？国内二线城市的住宿上限是多少？", language: "zh-CN", max_sources: 3, context_hint: "HR policy" },
        attachments: []
      }
    ],
    output_examples: [
      {
        title: "有据可查的回答",
        result: {
          answer: "根据公司差旅管理规定（2024年修订版），国内二线城市的住宿报销上限为每晚 450 元人民币（税后）。",
          confidence: 0.92,
          sources: [
            { title: "差旅管理规定（2024年修订版）- 第3章 住宿标准", excerpt: "二线城市住宿费用报销上限为每晚人民币450元（税后），超标须提前审批。", relevance_score: 0.97 },
            { title: "差旅管理规定（2024年修订版）- 第4章 差旅补贴", excerpt: "国内出差每日补贴标准：餐费及市内交通合计150元，凭票据实报实销，每日上限200元。", relevance_score: 0.88 }
          ],
          follow_up_questions: ["差旅报销流程需要提交哪些材料？", "超标住宿的审批流程是什么？"],
          retrieval_coverage: "full"
        },
        attachments: []
      }
    ],
    readme_markdown: "# Atlas 企业知识问答\n\n提问，从企业私有知识库获得有据可查的回答与引用来源。问题是公开的，知识是私有的。私有知识语料库、检索策略、嵌入模型和重排序算法全部私有运行，无文件附件。",
    template_version: "1.0.0"
  },
  "pixel.product.renderer.v1": {
    template_ref: "docs/templates/hotlines/pixel.product.renderer.v1/",
    input_schema: {
      type: "object",
      required: ["product_description"],
      properties: {
        product_description: { type: "string", description: "产品描述（材质、颜色、关键特征、渲染需求等）", minLength: 10, maxLength: 1500 },
        background_style: { type: "string", enum: ["pure_white", "gradient_soft", "lifestyle_studio", "outdoor_natural", "dark_premium", "transparent"], default: "pure_white", description: "背景风格" },
        angle: { type: "string", enum: ["front", "three_quarter", "top_down", "side", "hero_45"], default: "three_quarter", description: "主要拍摄角度" },
        resolution: { type: "string", enum: ["1024x1024", "1500x1500", "2048x2048", "1080x1350"], default: "1500x1500", description: "输出分辨率预设" },
        quantity: { type: "integer", minimum: 1, maximum: 4, default: 1, description: "生成渲染图数量（1–4）" }
      },
      additionalProperties: false
    },
    output_schema: {
      type: "object",
      required: ["renders", "metadata"],
      properties: {
        renders: {
          type: "array",
          minItems: 1,
          items: {
            type: "object",
            required: ["attachment_role", "width", "height", "format"],
            properties: {
              attachment_role: { type: "string" },
              width: { type: "integer" },
              height: { type: "integer" },
              format: { type: "string" },
              background_applied: { type: "string" },
              angle_applied: { type: "string" }
            }
          }
        },
        metadata: {
          type: "object",
          required: ["render_time_ms"],
          properties: {
            render_time_ms: { type: "integer" },
            model_version: { type: "string" },
            background_removal_applied: { type: "boolean" }
          }
        }
      },
      additionalProperties: false
    },
    input_attachments: {
      accepts_files: true,
      max_files: 1,
      max_total_size_bytes: 15728640,
      accepted_mime_types: ["image/jpeg", "image/png", "image/webp"],
      file_roles: [
        { role: "product_photo", required: true, description: "待渲染的产品照片（JPEG/PNG/WebP，最大 15MB）", accepted_types: ["image/jpeg", "image/png", "image/webp"], max_size_bytes: 15728640 }
      ]
    },
    output_attachments: {
      includes_files: true,
      max_files: 4,
      max_total_size_bytes: 52428800,
      possible_mime_types: ["image/jpeg", "image/png", "image/webp"],
      file_roles: [
        { role: "rendered_image_1", guaranteed: true, description: "第一张渲染图（quantity >= 1 时必返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] },
        { role: "rendered_image_2", guaranteed: false, description: "第二张渲染图（quantity >= 2 时返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] },
        { role: "rendered_image_3", guaranteed: false, description: "第三张渲染图（quantity >= 3 时返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] },
        { role: "rendered_image_4", guaranteed: false, description: "第四张渲染图（quantity = 4 时返回）", possible_types: ["image/jpeg", "image/png", "image/webp"] }
      ]
    },
    input_examples: [
      {
        title: "蓝牙耳机产品渲染",
        description: "上传一张耳机照片，生成深色高端背景的产品渲染图",
        params: { product_description: "一款黑色哑光铝合金外壳的蓝牙耳机，弧形头梁，带软垫耳罩，侧面有金属品牌标识。展示科技感和高端质感。", background_style: "dark_premium", angle: "three_quarter", resolution: "1500x1500", quantity: 2 },
        attachments: [{ role: "product_photo", filename: "headphone_raw.jpg", mime_type: "image/jpeg" }]
      }
    ],
    output_examples: [
      {
        title: "双角度渲染结果",
        result: {
          renders: [
            { attachment_role: "rendered_image_1", width: 1500, height: 1500, format: "jpeg", background_applied: "dark_premium", angle_applied: "three_quarter" },
            { attachment_role: "rendered_image_2", width: 1500, height: 1500, format: "jpeg", background_applied: "dark_premium", angle_applied: "hero_45" }
          ],
          metadata: { render_time_ms: 22400, model_version: "pixel-renderer-v2.4", background_removal_applied: true }
        },
        attachments: [
          { role: "rendered_image_1", filename: "render_three_quarter.jpg", mime_type: "image/jpeg" },
          { role: "rendered_image_2", filename: "render_hero_45.jpg", mime_type: "image/jpeg" }
        ]
      }
    ],
    readme_markdown: "# Pixel 产品图渲染器\n\n上传产品照片，私有微调渲染模型生成高质量电商视觉素材。产品是公开的，渲染智能是私有的。微调渲染模型、背景合成算法、多道合成管线和后处理预设全部私有运行。",
    template_version: "1.0.0"
  }
};

export const MARKETPLACE_API_BASE_URL = (import.meta.env.VITE_MARKETPLACE_API_BASE_URL as string | undefined)?.trim() || "";

export function slugifyMarketplaceSegment(value: string | null | undefined) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return normalized || "item";
}

export function normalizeHotline(item: MarketplaceHotline): MarketplaceHotline {
  return {
    ...item,
    responder_slug: item.responder_slug || slugifyMarketplaceSegment(item.responder_id),
    hotline_slug: item.hotline_slug || slugifyMarketplaceSegment(item.display_name || item.hotline_id)
  };
}

export function buildMarketplacePath(item: MarketplaceHotline) {
  const normalized = normalizeHotline(item);
  return `/marketplace/responders/${normalized.responder_slug}/${normalized.hotline_slug}`;
}

export function buildResponderPath(responder: MarketplaceResponder | MarketplaceHotline) {
  const responderSlug =
    "responder_slug" in responder && responder.responder_slug ? responder.responder_slug : slugifyMarketplaceSegment(responder.responder_id);
  return `/marketplace/responders/${responderSlug}`;
}

export function uniqueResponders(items: MarketplaceHotline[]): MarketplaceResponder[] {
  const map = new Map<string, MarketplaceResponder>();
  for (const item of items.map(normalizeHotline)) {
    const current = map.get(item.responder_id);
    if (!current) {
      map.set(item.responder_id, {
        responder_id: item.responder_id,
        responder_slug: item.responder_slug,
        display_name: item.responder_id,
        hotline_count: 1,
        capabilities: [...(item.capabilities || [])],
        hotlines: [item]
      });
      continue;
    }
    current.hotline_count += 1;
    current.capabilities = Array.from(new Set([...current.capabilities, ...(item.capabilities || [])]));
    current.hotlines = [...(current.hotlines || []), item];
  }
  return Array.from(map.values());
}

export async function requestMarketplaceJson<T>(pathname: string): Promise<T> {
  const baseUrl = MARKETPLACE_API_BASE_URL || window.location.origin;
  const response = await fetch(new URL(pathname, baseUrl));
  if (!response.ok) {
    throw new Error(`marketplace_${response.status}`);
  }
  return response.json();
}

export function requestHotlineCatalog() {
  return requestMarketplaceJson<{ items: MarketplaceHotline[] }>("/marketplace/hotlines");
}

export function requestHotlineDetail(hotlineId: string) {
  return requestMarketplaceJson<MarketplaceHotline>(`/marketplace/hotlines/${hotlineId}`);
}

export function requestHotlineTemplateBundle(hotlineId: string, templateRef: string) {
  const encodedTemplateRef = encodeURIComponent(templateRef);
  return requestMarketplaceJson<MarketplaceTemplateBundle>(
    `/marketplace/hotlines/${hotlineId}/template-bundle?template_ref=${encodedTemplateRef}`
  );
}
